<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <div class="my-auto mb-2">
          <h2 class="mb-1">{{ pageTitle }}</h2>
          <p v-if="isPureEmployee" class="text-muted small mb-2">
            Updates about your payslips, leave, and other items that concern you.
          </p>
          <nav>
            <ol class="breadcrumb mb-0">
              <li class="breadcrumb-item">
                <router-link :to="dashboardRoute">Dashboard</router-link>
              </li>
              <li class="breadcrumb-item active" aria-current="page">
                {{ isPureEmployee ? "Your notifications" : "Notifications" }}
              </li>
            </ol>
          </nav>
        </div>
        <div class="d-flex align-items-center gap-2">
          <button class="btn btn-light" type="button" @click="refreshAll" :disabled="store.loading || store.dropdownLoading">
            <i class="ti ti-refresh me-1"></i>Refresh
          </button>
          <button class="btn btn-outline-primary" type="button" @click="markAllRead" :disabled="store.unreadCount === 0 || store.actionLoading">
            <i class="ti ti-checks me-1"></i>{{ isPureEmployee ? "Mark all as read" : "Mark All Read" }}
          </button>
          <button
            v-if="!isPureEmployee"
            class="btn btn-outline-danger"
            type="button"
            @click="clearAll"
            :disabled="store.meta.total === 0 || store.actionLoading"
          >
            <i class="ti ti-trash me-1"></i>Clear All
          </button>
        </div>
      </div>

      <div
        v-if="isPureEmployee"
        class="alert alert-light border d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3"
        role="status"
      >
        <span class="text-muted small mb-0">
          <strong class="text-gray-9">{{ store.unreadCount }}</strong>
          unread
          <span v-if="store.meta.total"> · {{ store.meta.total }} total</span>
        </span>
        <button
          v-if="filters.status === 'unread'"
          class="btn btn-link btn-sm p-0 text-decoration-none"
          type="button"
          @click="showAllNotifications"
        >
          Show all
        </button>
        <button
          v-else
          class="btn btn-link btn-sm p-0 text-decoration-none"
          type="button"
          @click="showUnreadOnly"
        >
          Show unread only
        </button>
      </div>

      <div v-if="!isPureEmployee" class="row g-3 mb-3">
        <div class="col-xl-4 col-md-6">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body">
              <span class="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-transparent text-primary mb-3 dq-stat-icon">
                <i class="ti ti-bell"></i>
              </span>
              <p class="text-muted mb-1">Total Notifications</p>
              <h3 class="mb-0">{{ store.meta.total || 0 }}</h3>
            </div>
          </div>
        </div>
        <div class="col-xl-4 col-md-6">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body">
              <span class="d-inline-flex align-items-center justify-content-center rounded-circle bg-warning-transparent text-warning mb-3 dq-stat-icon">
                <i class="ti ti-bell-ringing"></i>
              </span>
              <p class="text-muted mb-1">Unread</p>
              <h3 class="mb-0">{{ store.unreadCount }}</h3>
            </div>
          </div>
        </div>
        <div class="col-xl-4 col-md-12">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body">
              <span class="d-inline-flex align-items-center justify-content-center rounded-circle bg-success-transparent text-success mb-3 dq-stat-icon">
                <i class="ti ti-check"></i>
              </span>
              <p class="text-muted mb-1">Read</p>
              <h3 class="mb-0">{{ readCount }}</h3>
            </div>
          </div>
        </div>
      </div>

      <div class="card border-0 shadow-sm mb-3">
        <div class="card-body">
          <div class="row g-3 align-items-end">
            <div :class="isPureEmployee ? 'col-lg-8' : 'col-lg-4'">
              <label class="form-label">Search</label>
              <input
                v-model.trim="filters.q"
                type="text"
                class="form-control"
                :placeholder="isPureEmployee ? 'Search your notifications' : 'Search title or message'"
                @keyup.enter="applyFilters"
              />
            </div>
            <div :class="isPureEmployee ? 'col-lg-4' : 'col-lg-3'">
              <label class="form-label">Status</label>
              <select v-model="filters.status" class="form-select" @change="applyFilters">
                <option value="all">All</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
              </select>
            </div>
            <div v-if="!isPureEmployee" class="col-lg-3">
              <label class="form-label">Category</label>
              <select v-model="filters.category" class="form-select">
                <option value="">All Categories</option>
                <option value="employee">Employee</option>
                <option value="leave">Leave</option>
                <option value="payroll">Payroll</option>
                <option value="birthday">Birthday</option>
                <option value="general">General</option>
              </select>
            </div>
            <div :class="isPureEmployee ? 'col-12 d-none' : 'col-lg-2'">
              <div class="d-flex gap-2">
                <button class="btn btn-primary w-100" type="button" @click="applyFilters">
                  <i class="ti ti-search me-1"></i>Apply
                </button>
                <button class="btn btn-light" type="button" @click="clearFilters">Clear</button>
              </div>
            </div>
            <div v-if="isPureEmployee" class="col-12">
              <button class="btn btn-primary btn-sm" type="button" @click="applyFilters">
                <i class="ti ti-search me-1"></i>Search
              </button>
              <button
                v-if="filters.q"
                class="btn btn-light btn-sm ms-2"
                type="button"
                @click="clearFilters"
              >
                Clear search
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="card border-0 shadow-sm">
        <div class="card-header d-flex align-items-center justify-content-between">
          <h5 class="mb-0">{{ isPureEmployee ? "Your updates" : "All Notifications" }}</h5>
          <span class="text-muted small">{{ store.meta.total || 0 }} item(s)</span>
        </div>

        <div v-if="store.loading" class="card-body">
          <div class="d-flex align-items-center text-muted">
            <span class="spinner-border spinner-border-sm me-2"></span>
            Loading notifications...
          </div>
        </div>

        <div v-else-if="!store.items.length" class="card-body">
          <div class="text-center py-5">
            <div class="avatar avatar-xl bg-light text-muted rounded-circle mx-auto mb-3">
              <i class="ti ti-bell-off fs-24"></i>
            </div>
            <h6 class="mb-1">{{ isPureEmployee ? "Nothing here yet" : "No notifications found" }}</h6>
            <p class="text-muted mb-0">
              {{ isPureEmployee ? "When something needs your attention, it will show up here." : "You are all caught up for now." }}
            </p>
          </div>
        </div>

        <div v-else class="card-body p-0">
          <div class="list-group list-group-flush">
            <div
              v-for="item in store.items"
              :key="item.id"
              class="list-group-item px-4 py-3 dq-notification-row"
              :class="{ 'dq-unread': !item.is_read }"
            >
              <div class="d-flex gap-3 align-items-start">
                <span class="d-inline-flex align-items-center justify-content-center rounded-circle dq-notification-icon" :class="iconClass(item.color)">
                  <i :class="`ti ti-${item.icon || 'bell'}`"></i>
                </span>

                <div class="flex-grow-1 min-w-0">
                  <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-1">
                    <div class="d-flex align-items-center gap-2 flex-wrap">
                      <h6 class="mb-0">{{ displayTitle(item) }}</h6>
                      <span class="badge rounded-pill" :class="item.is_read ? 'bg-light text-muted' : 'bg-warning-transparent text-warning'">
                        {{ item.is_read ? "Read" : "Unread" }}
                      </span>
                      <span
                        v-if="!isPureEmployee"
                        class="badge bg-light text-dark text-capitalize"
                      >{{ item.category || "general" }}</span>
                    </div>
                    <small class="text-muted">{{ item.created_human }}</small>
                  </div>
                  <p class="mb-2 text-muted">{{ displayMessage(item) }}</p>
                  <div class="d-flex flex-wrap gap-2">
                    <button
                      v-if="!item.is_read"
                      class="btn btn-sm btn-outline-primary"
                      type="button"
                      @click="markRead(item)"
                    >
                      <i class="ti ti-check me-1"></i>{{ isPureEmployee ? "Mark as read" : "Mark Read" }}
                    </button>
                    <button
                      v-if="item.action_url"
                      class="btn btn-sm btn-primary"
                      type="button"
                      @click="openNotification(item)"
                    >
                      <i class="ti ti-arrow-right me-1"></i>{{ openButtonLabel(item) }}
                    </button>
                    <button
                      v-if="!isPureEmployee"
                      class="btn btn-sm btn-outline-danger"
                      type="button"
                      @click="deleteNotification(item)"
                    >
                      <i class="ti ti-trash me-1"></i>Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="store.meta.last_page > 1" class="card-footer d-flex align-items-center justify-content-between">
          <span class="text-muted small">
            Page {{ store.meta.current_page }} of {{ store.meta.last_page }}
          </span>
          <div class="d-flex gap-2">
            <button class="btn btn-light btn-sm" type="button" :disabled="store.meta.current_page <= 1" @click="changePage(store.meta.current_page - 1)">
              Previous
            </button>
            <button class="btn btn-light btn-sm" type="button" :disabled="store.meta.current_page >= store.meta.last_page" @click="changePage(store.meta.current_page + 1)">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/authStore";
import { useNotificationStore } from "@/stores/notificationStore";
import layoutHeader from "@/views/layouts/layout-header.vue";
import layoutSidebar from "@/views/layouts/layout-sidebar.vue";

const router = useRouter();
const auth = useAuthStore();
const store = useNotificationStore();

const filters = reactive({
  status: store.filters.status || "all",
  category: store.filters.category || "",
  q: store.filters.q || "",
});

const readCount = computed(() =>
  Math.max((store.meta.total || 0) - (store.unreadCount || 0), 0),
);

function normalizeRoles(user) {
  return (user?.roles || [])
    .map((r) => (typeof r === "string" ? r : r?.name))
    .filter(Boolean);
}

const isPureEmployee = computed(() => {
  const roles = normalizeRoles(auth.user);
  return (
    roles.includes("Employee") &&
    !roles.includes("Admin") &&
    !roles.includes("Hr") &&
    !roles.includes("HR") &&
    !roles.includes("SuperAdmin")
  );
});

const pageTitle = computed(() =>
  isPureEmployee.value ? "Your notifications" : "Notifications",
);

const dashboardRoute = computed(() => {
  const roles = normalizeRoles(auth.user);
  if (roles.includes("SuperAdmin")) return "/super-admin/dashboard";
  return isPureEmployee.value
    ? "/dashboard/employee-dashboard"
    : "/dashboard/admin-dashboard";
});

function isLegacyPayrollApproved(item) {
  const type = item?.type || "";
  const title = String(item?.title || "").toLowerCase();
  return type === "payroll.approved" || title === "payroll approved";
}

function displayTitle(item) {
  if (isLegacyPayrollApproved(item)) {
    return "Your payslip is ready";
  }
  return item?.title || "Notification";
}

function displayMessage(item) {
  if (isLegacyPayrollApproved(item)) {
    const period =
      item?.meta?.period ||
      extractPeriodFromPayrollMessage(item?.message) ||
      "";
    if (period) {
      return `Your payslip for ${period} is ready to view.`;
    }
    return "Your payslip is ready to view.";
  }
  return item?.message || "";
}

function extractPeriodFromPayrollMessage(message) {
  const match = String(message || "").match(
    /Payroll for (\d{4}-\d{2}|\w+ \d{4})/i,
  );
  return match?.[1] || "";
}

function openButtonLabel(item) {
  if (isPureEmployee.value) {
    const url = item?.action_url || "";
    if (url.includes("my-payslips")) return "View payslip";
    if (url.includes("my-documents")) return "Open documents";
    if (url.includes("leaves")) return "View leave";
  }
  return "Open";
}

async function showAllNotifications() {
  filters.status = "all";
  await load(1);
}

async function showUnreadOnly() {
  filters.status = "unread";
  await load(1);
}

function iconClass(color) {
  return {
    "bg-primary-transparent text-primary": color === "primary",
    "bg-success-transparent text-success": color === "success",
    "bg-warning-transparent text-warning": color === "warning",
    "bg-danger-transparent text-danger": color === "danger",
    "bg-info-transparent text-info": color === "info",
  };
}

async function load(page = 1) {
  store.filters = {
    ...store.filters,
    ...filters,
    category: isPureEmployee.value ? "" : filters.category,
    page,
  };
  await store.fetchNotifications({ page });
  await store.fetchRecent();
}

async function refreshAll() {
  await Promise.all([store.fetchRecent(), load(store.meta.current_page || 1)]);
}

async function applyFilters() {
  await load(1);
}

async function clearFilters() {
  filters.status = isPureEmployee.value ? "unread" : "all";
  filters.category = "";
  filters.q = "";
  await load(1);
}

async function markRead(item) {
  await store.markRead(item.id);
}

async function markAllRead() {
  await store.markAllRead();
}

async function deleteNotification(item) {
  await store.deleteNotification(item.id);
}

async function clearAll() {
  await store.clearAll();
}

async function openNotification(item) {
  if (!item.is_read) {
    await store.markRead(item.id, true);
  }

  if (item.action_url) {
    router.push(item.action_url);
  }
}

async function changePage(page) {
  await load(page);
}

onMounted(async () => {
  if (isPureEmployee.value && filters.status === "all" && !filters.q) {
    filters.status = "unread";
  }
  await load(1);
});
</script>

<style scoped>
.dq-stat-icon {
  width: 44px;
  height: 44px;
  font-size: 18px;
}

.dq-notification-row {
  transition: background-color 0.2s ease;
}

.dq-notification-row:hover {
  background: rgba(15, 23, 42, 0.02);
}

.dq-unread {
  background: rgba(255, 193, 7, 0.06);
}

.dq-notification-icon {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  font-size: 18px;
}

.min-w-0 {
  min-width: 0;
}
</style>
